import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {DataService} from "../services/data.service";
import moment from "moment/moment";
import {DatePipe, DecimalPipe, NgForOf, NgIf} from "@angular/common";
import {ToastrService} from "ngx-toastr";
import Swal from "sweetalert2";
import {BaseChartDirective} from "ng2-charts";
import {ChartConfiguration} from "chart.js";
import {ActivatedRoute} from '@angular/router';
import {AccessService} from "../services/access.service";
import {TooltipDirective} from "ngx-bootstrap/tooltip";
import {ThemeService} from "../services/theme.service";

@Component({
    selector: 'app-turnover',
    imports: [ReactiveFormsModule, FormsModule, NgForOf, DecimalPipe, NgIf, BaseChartDirective, DatePipe, TooltipDirective],
    template: `
        <div class="card">
            <div class="card-body">
                <div class="mb-4">
                    <h3><i class="fa-solid fa-money-bill me-2"></i>Umsätze</h3>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-2">
                    <select class="form-control" style="width: 160px" [(ngModel)]="id_region" (ngModelChange)="loadData()">
                        <option value="" selected>Alle Regionen</option>
                        <option *ngFor="let region of regions" [value]="region.id">{{ region.name }}</option>
                    </select>

                    <select class="form-control" style="width: 160px" [(ngModel)]="id_store" (ngModelChange)="loadData()">
                        <option value="" selected>Alle Stores</option>
                        <option *ngFor="let store of stores" [value]="store.id">{{ store.name }}</option>
                    </select>

                    <select class="form-control" style="width: 160px" [(ngModel)]="id_data_type" (ngModelChange)="loadData()">
                        <option value="" selected>Alle Typen</option>
                        <option *ngFor="let dataType of dataTypes" [value]="dataType.id">{{ dataType.name }}</option>
                    </select>

                    <div class="input-group" style="width: 170px">
                        <div class="input-group-prepend">
                            <button class="btn btn-outline-secondary" type="button" (click)="setYear(-1)">
                                <i class="fa-solid fa-arrow-left"></i>
                            </button>
                        </div>
                        <input type="number" class="form-control" name="year"
                               [(ngModel)]="year"
                               [max]="currentYear"
                               (ngModelChange)="loadData()"/>
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary" type="button" [disabled]="year == currentYear" (click)="setYear(1)">
                                <i class="fa-solid fa-arrow-right"></i>
                            </button>
                        </div>
                    </div>

                    <div class="input-group" style="width: 200px">
                        <div class="input-group-prepend">
                            <button class="btn btn-outline-secondary" type="button" (click)="setMonth(-1)" [disabled]="month === ''">
                                <i class="fa-solid fa-arrow-left"></i>
                            </button>
                        </div>
                        <select class="form-control" name="month" [(ngModel)]="month" (ngModelChange)="loadData()">
                            <option value="" selected>Alle Monate</option>
                            <option *ngFor="let month of monthNames; let i = index" [value]="i">{{ month }}</option>
                        </select>
                        <div class="input-group-append">
                            <button class="btn btn-outline-secondary" type="button" (click)="setMonth(1)" [disabled]="month === ''">
                                <i class="fa-solid fa-arrow-right"></i>
                            </button>
                        </div>
                    </div>

                    @if (month !== '') {
                        <div class="input-group" style="width: 165px">
                            <div class="input-group-prepend">
                                <button class="btn btn-outline-secondary" type="button" (click)="setDay(-1)" [disabled]="day === ''">
                                    <i class="fa-solid fa-arrow-left"></i>
                                </button>
                            </div>
                            <select class="form-control" [(ngModel)]="day" (ngModelChange)="loadData()">
                                <option value="" selected>Alle Tage</option>
                                <option *ngFor="let day of [].constructor(daysInMonth); let i = index" [value]="i + 1">{{ i + 1 }}</option>
                            </select>
                            <div class="input-group-append">
                                <button class="btn btn-outline-secondary" type="button" (click)="setDay(1)" [disabled]="day === ''">
                                    <i class="fa-solid fa-arrow-right"></i>
                                </button>
                            </div>
                        </div>
                    }

                    @if (!showForm) {
                        <button type="button" class="btn btn-primary" (click)="setCurrentTurnover()">
                            <i class="fa-solid fa-plus"></i> Umsatz hinzufügen
                        </button>
                    }
                </div>

                <ul class="nav nav-tabs my-3">
                    <li class="nav-item" (click)="setTab($event, 'chart')">
                        <a class="nav-link" [class.active]="currentTab == 'chart'" href>Chart</a>
                    </li>
                    <li class="nav-item" (click)="setTab($event, 'table')">
                        <a class="nav-link" [class.active]="currentTab == 'table'" href>Tabelle</a>
                    </li>
                    <li class="nav-item" (click)="setTab($event, 'detail')">
                        <a class="nav-link" [class.active]="currentTab == 'detail'" href>Detailchart</a>
                    </li>
                </ul>

                <h3 class="mt-3 mb-0 text-center">
                    @if (day) {
                        {{ day }}.
                    }
                    {{ monthNames[month] }} {{ year }}
                </h3>

                @if (currentTab == 'chart') {
                    <select class="form-control mt-2" style="width: 200px" [(ngModel)]="chartSortOrder" (ngModelChange)="updateOriginalChart()">
                        <option value="name">Nach Name sortieren</option>
                        <option value="turnover">Nach Umsatz sortieren</option>
                    </select>

                    @if (total) {
                        <div class="fs-6 fw-bold mt-3">
                            Gesamtumsatz: € {{ total.turnover | number: '1.2-2' }}
                        </div>
                    }

                    @if (originalChartData && originalChart) {
                        <div [style.height.px]="originalChartData?.length * 15 + 200">
                            <canvas baseChart
                                    [data]="originalChart.data"
                                    [options]="originalChart.options"
                                    [type]="originalChart.type"
                                    class="mt-2">
                            </canvas>
                        </div>
                    }
                } @else if (currentTab == 'table') {
                    <h5 class="mt-3">Gesamt</h5>

                    @if (storesData?.length > 0) {
                        <div class="table-responsive">
                            <table class="table table-striped table-sm table-hover pointer">
                                <thead>
                                <tr>
                                    <th style="min-width: 80px">
                                        <button class="btn btn-xs btn-secondary" (click)="toggleAllData(true)">
                                            <i class="fa-solid fa-arrow-down"></i>
                                        </button>
                                        <button class="btn btn-xs btn-secondary ms-2" (click)="toggleAllData(false)">
                                            <i class="fa-solid fa-arrow-right"></i>
                                        </button>
                                    </th>
                                    <th>
                                        Store
                                    </th>
                                    <th class="text-end" style="width: 175px">
                                        Umsatz (€)
                                    </th>
                                    <th class="d-none d-md-table-cell" style="width: 100px">
                                        Wertart
                                    </th>
                                    <th class="text-end" style="width: 100px">
                                        Belege
                                    </th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th>
                                        Summe
                                    </th>
                                    <th class="text-end">
                                        {{ total.turnover | number: '1.2-2' }}
                                    </th>
                                    <th class="d-none d-md-table-cell"></th>
                                    <th class="text-end">
                                        {{ total.sales | number: '1.0-0' }}
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                    @for (data of storesData; track data.name) {
                                        <tr>
                                            <td>
                                                <i class="fa-solid me-2" [class.fa-arrow-right]="!data.toggled" [class.fa-arrow-down]="data.toggled" (click)="toggleData(data)"></i>
                                            </td>
                                            <td class="fw-bold">
                                                {{ data.name }}
                                            </td>
                                            <td class="text-end">
                                                {{ data.turnover | number: '1.2-2' }}
                                            </td>
                                            <td class="d-none d-md-table-cell">
                                                {{ data.value_type }}
                                            </td>
                                            <td class="text-end">
                                                {{ data.sales | number: '1.0-0' }}
                                            </td>
                                        </tr>
                                        @if (data.toggled) {
                                            @for (type of data.types; track type.name) {
                                                <tr>
                                                    <td></td>
                                                    <td>
                                                        {{ type.name }}
                                                        <i class="fa-solid fa-truck me-2 text-info" [tooltip]="'Lieferservice'" *ngIf="type.name === 'Lieferservice'"></i>
                                                        <i class="fa-solid fa-money-bill me-2 text-success" [tooltip]="'Bargeld'" *ngIf="type.name === 'Bargeld'"></i>
                                                        <i class="fa-solid fa-credit-card me-2 text-secondary" [tooltip]="'Kartenzahlung'" *ngIf="type.name === 'Kartenzahlung'"></i>
                                                    </td>
                                                    <td class="text-end">
                                                        {{ type.turnover | number: '1.2-2' }}
                                                    </td>
                                                    <td class="d-none d-md-table-cell">
                                                        {{ type.valueType }}
                                                    </td>
                                                    <td class="text-end">
                                                        {{ type.sales | number: '1.0-0' }}
                                                    </td>
                                                </tr>
                                            }
                                        }
                                    }
                                </tbody>
                            </table>
                        </div>
                    } @else {
                        <p>Keine Daten gefunden</p>
                    }

                    <h5>Manuell erstellte Einträge</h5>

                    @if (manualData?.length > 0) {
                        <div class="table-responsive">
                            <table class="table table-striped table-sm table-hover mb-0 pointer">
                                <thead>
                                <tr>
                                    <th style="min-width: 70px"></th>
                                    <th>
                                        Store
                                    </th>
                                    <th style="width: 220px">
                                        Erstellt von
                                    </th>
                                    <th style="width: 100px">
                                        Datum
                                    </th>
                                    <th class="text-end" style="width: 175px">
                                        Umsatz (€)
                                    </th>
                                    <th style="width: 100px">
                                        Wertart
                                    </th>
                                    <th class="text-end" style="width: 100px">
                                        Belege
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr *ngFor="let data of manualData; let i = index">
                                    <td>
                                        @if (data.id_user != null) {
                                            <button type="button" class="btn btn-xs btn-danger me-2"
                                                    (click)="deleteTurnover(data)">
                                                <i class="fa-solid fa-trash"></i>
                                            </button>
                                            <button type="button" class="btn btn-xs btn-primary" (click)="setCurrentTurnover(data)">
                                                <i class="fa-solid fa-pencil"></i>
                                            </button>
                                        }
                                    </td>
                                    <td>
                                        {{ data.name }}
                                        <i class="fa-solid fa-truck me-2 text-info" [tooltip]="'Lieferservice'" *ngIf="data.data_type === 'Lieferservice'"></i>
                                        <i class="fa-solid fa-money-bill me-2 text-success" [tooltip]="'Bargeld'" *ngIf="data.data_type === 'Bargeld'"></i>
                                        <i class="fa-solid fa-credit-card me-2 text-secondary" [tooltip]="'Kartenzahlung'" *ngIf="data.data_type === 'Kartenzahlung'"></i>
                                    </td>
                                    <td>
                                        {{ data.user_name }}
                                    </td>
                                    <td>
                                        {{ data.date | date: 'dd.MM.yyyy' }}
                                    </td>
                                    <td class="text-end">
                                        {{ data.turnover | number: '1.2-2' }}
                                    </td>
                                    <td class="d-none d-md-table-cell">
                                        {{ data.value_type }}
                                    </td>
                                    <td class="text-end">
                                        {{ data.sales | number: '1.0-0' }}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    } @else {
                        <div>Keine manuell erstellten Einträge gefunden</div>
                    }
                } @else {
                    <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
                        <button type="button" class="btn btn-sm btn-secondary" (click)="toggleTurnoverLabels()">
                            <i class="fa-solid me-2" [class.fa-eye]="showTurnoverLabels" [class.fa-eye-slash]="!showTurnoverLabels"></i>
                            Umsatz Labels
                        </button>

                        <button type="button" class="btn btn-sm btn-secondary" (click)="toggleSalesLabels()">
                            <i class="fa-solid me-2" [class.fa-eye]="showSalesLabels" [class.fa-eye-slash]="!showSalesLabels"></i>
                            Belege Labels
                        </button>
                    </div>

                    <div style="height: 500px">
                        <canvas baseChart
                                [data]="chart.data"
                                [options]="chart.options"
                                [type]="chart.type"
                                class="mt-3">
                        </canvas>
                    </div>
                }

                <div *ngIf="showForm" class="card mt-3">
                    <div class="card-body">
                        @if (currentTurnover.id_user) {
                            <h5 class="card-title mb-3">Umsatz bearbeiten</h5>
                        } @else {
                            <h5 class="card-title mb-3">
                                Umsatz hinzufügen
                            </h5>
                        }

                        <form #turnoverForm="ngForm">
                            <div class="row mb-3">
                                <div class="col-md-6">
                                    <label for="store" class="form-label">Store <span class="text-danger">*</span></label> <i class="fas fa-circle-info text-primary ms-2" [tooltip]="'Es stehen nur Stores zur Auswahl die Sonderumsätze aktiviert haben!'"></i>
                                    <select id="store" class="form-control" name="id_store" required
                                            [(ngModel)]="currentTurnover.id_store"
                                            [disabled]="currentTurnover.id_user">
                                        <option value="" disabled selected style="display: none;">Bitte auswählen</option>
                                        <option *ngFor="let store of specialStores" [value]="store.id">{{ store.name }}</option>
                                    </select>
                                </div>
                                <div class="col-md-3">
                                    <label for="date" class="form-label">Datum <span class="text-danger">*</span></label>
                                    <input type="date" id="date" class="form-control" name="date" required
                                           [(ngModel)]="currentTurnover.date"
                                           (ngModelChange)="setAvailableTypes()"
                                           [disabled]="currentTurnover.id_user">
                                </div>
                            </div>
                            <div class="row mb-3">
                                <div class="col-md-3">
                                    <label for="type" class="form-label">Typ <span class="text-danger">*</span></label>
                                    <select id="type" class="form-control"  name="type" required [(ngModel)]="currentTurnover.type" [disabled]="currentTurnover.id_user">
                                        <option value="delivery">Lieferservice</option>
                                        <option value="card">Kartenzahlung</option>
                                        <option value="cash">Bargeld</option>
                                    </select>
                                </div>
                                <div class="col-md-3">
                                    <label for="value" class="form-label">Umsatz (€, {{ currentTurnover.type == 'delivery' ? 'Brutto' : 'Netto' }}) <span class="text-danger">*</span></label>
                                    <input type="number" id="value" class="form-control" [(ngModel)]="currentTurnover.value" name="value" min="1" step="0.01" placeholder="0.00" required>
                                </div>
                                <div class="col-md-3">
                                    <label for="value" class="form-label">Belege <span class="text-danger">*</span></label>
                                    <input type="number" id="value" class="form-control" [(ngModel)]="currentTurnover.count" name="count" min="1" required>
                                </div>
                            </div>
                            <div class="d-flex gap-2">
                                @if (!currentTurnover.id_user) {
                                    <button type="button" class="btn btn-primary" [disabled]="!turnoverForm.valid" (click)="saveTurnover()">
                                        <i class="fa-solid fa-save me-1"></i>Speichern
                                    </button>
                                } @else {
                                    <button type="button" class="btn btn-primary" [disabled]="!turnoverForm.valid" (click)="updateTurnover()">
                                        <i class="fa-solid fa-save me-1"></i>Aktualisieren
                                    </button>
                                }
                                <button type="button" class="btn btn-secondary" (click)="closeCurrentTurnover()">
                                    <i class="fa-solid fa-times me-1"></i>Abbrechen
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    `
})
export class TurnoverComponent implements OnInit {

    @ViewChild(BaseChartDirective) baseChart?: BaseChartDirective;

    id_region = '';
    id_store = '';
    id_data_type = '';

    year: any;
    currentYear!: number;
    month: any;
    currentMonth!: number;
    day: any;
    currentDay!: number;
    daysInMonth: number;

    regions!: any[];
    stores!: any[];
    dataTypes!: any[];
    specialStores: any[] = [];
    total: any;
    storesData: any[] = [];
    manualData: any[] = [];
    chartData: any[] = [];
    originalChartData: any[] = [];
    allToggled = false;
    toggleState: any = {};

    currentTurnover: any = {};
    showForm = false;

    predicate = 'turnover';
    reverse: boolean = true;

    currentTab = 'chart';

    monthNames = [
        'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];

    chart!: ChartConfiguration;
    originalChart!: ChartConfiguration;
    chartSortOrder = 'turnover';

    availableTypes: any[];
    showTurnoverLabels = true;
    showSalesLabels = true;

    private dataService = inject(DataService);
    private toastrService = inject(ToastrService);
    private route = inject(ActivatedRoute);
    private accessService = inject(AccessService);
    private themeService = inject(ThemeService);

    ngOnInit(): void {
        this.currentYear = moment().year();
        this.currentMonth = moment().month();
        this.currentDay = moment().day();
        this.year = moment().year();
        this.month = moment().month();
        this.day = moment().date() - 1;

        this.calculateDaysInMonth();

        // Check for query parameters
        this.route.queryParams.subscribe(params => {
            if (params['id_store']) {
                this.id_store = params['id_store'];
            }
        });

        this.loadData();

        this.dataService.request("Store/listForSelection").subscribe(response => {
            this.stores = response.stores;
            this.specialStores = this.stores.filter(store => store.special_sales == 1);
        })

        this.dataService.request("Region/listForSelection").subscribe(response => {
            this.regions = response.regions;
        })

        this.dataService.request("Data/listDataTypes").subscribe(response => {
            this.dataTypes = response.dataTypes;
        })
    }

    calculateDaysInMonth() {
        if (this.month === '') return;

        this.daysInMonth = moment([this.year, this.month]).daysInMonth();
        if (this.day > this.daysInMonth) {
            this.day = this.daysInMonth;
        }
    }

    setOrder(order: string): void {
        this.predicate = order;
        this.reverse = !this.reverse;
        this.loadData();
    }

    setYear(number: number) {
        this.year += number;

        /*if (this.year == this.currentYear && this.month >= this.currentMonth) {
            this.month = this.currentMonth - 1;
        }*/

        this.loadData();
    }

    setMonth(number: number) {
        this.month = parseInt(this.month) + number;

        if (this.month < 0) {
            this.month = 11;
            this.setYear(-1);
        } else if (this.month > 11) {
            this.month = 0;
            this.setYear(1)
        } else {
            this.loadData();
        }
    }

    setDay(number: number) {
        this.day = parseInt(this.day) + number;

        if (this.day < 1) {
            this.day = 31;
            this.setMonth(-1);
        } else if (this.day > this.daysInMonth) {
            this.day = 1;
            this.setMonth(1);
        } else {
            this.loadData();
        }
    }

    loadData() {
        if (this.month === '') {
            this.day = '';
        }

        this.calculateDaysInMonth();

        this.dataService.request("Turnover/getData", {
            id_region: this.id_region,
            id_store: this.id_store,
            id_data_type: this.id_data_type,
            year: this.year,
            month: this.month !== "" ? parseInt(this.month) + 1 : '',
            day: this.day,
            order: this.predicate,
            reverse: this.reverse,
        }).subscribe(response => {
            this.storesData = response.stores;
            this.manualData = response.manualData;
            this.chartData = response.chartData;
            this.originalChartData = response.originalChartData;

            this.total = {
                turnover: 0,
                sales: 0,
            }
            this.storesData.forEach(store => {
                store.toggled = this.allToggled;

                if (this.toggleState[store.name] != undefined) store.toggled = this.toggleState[store.name];

                store.turnover = parseFloat(store.turnover);
                store.sales = parseInt(store.sales);

                this.total.turnover += store.turnover;
                this.total.sales += store.sales;
            });

            this.updateChart();
            this.updateOriginalChart();
        })
    }

    updateOriginalChart() {
        // Aggregate turnovers by store name
        const aggregatedData = this.originalChartData.reduce((acc: any, store: any) => {
            if (acc[store.name]) {
                acc[store.name] += store.turnover;
            } else {
                acc[store.name] = store.turnover;
            }
            return acc;
        }, {});

        // Sort the aggregated data based on chartSortOrder
        const entries = Object.entries(aggregatedData);
        entries.sort((a: any, b: any) => {
            if (this.chartSortOrder === 'name') {
                return a[0].localeCompare(b[0]);
            } else if (this.chartSortOrder === 'turnover') {
                return b[1] - a[1]; // Descending order for turnover
            }
            return 0;
        });

        const storeNames: string[] = entries.map(entry => entry[0]);
        const turnovers: any[] = entries.map(entry => entry[1]);

        this.originalChart = {
            data: {
                labels: storeNames,
                datasets: [
                    {
                        label: 'Umsatz (€)',
                        data: turnovers,
                        backgroundColor: this.accessService.client.primaryColor,
                        borderColor: this.accessService.client.primaryColor,
                        borderRadius: 8,
                        borderSkipped: 'start',
                        barPercentage: 0.5,
                    }
                ],
            },
            options: {
                indexAxis: 'y',
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        right: 40
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        enabled: false
                    },
                    datalabels: {
                        anchor: 'end',
                        align: 'right',
                        color: this.themeService.isDarkMode ? '#fff' : '#000',
                        font: {
                            weight: 'bold'
                        },
                        formatter: (value: number) => {
                            if (value == 0) return '';
                            return '€ ' + Number(value).toLocaleString('de-DE', {maximumFractionDigits: 0});
                        }
                    }
                },
                elements: {
                    bar: {
                        borderRadius: 8,
                        borderSkipped: false,
                    }
                },
            },
            type: 'bar',
        };
    }

    updateChart() {

        // Extract unique type names for datasets
        const typeNames = new Set<string>();
        this.chartData.forEach(item => {
            item.types.forEach((type: any) => typeNames.add(type.name));
        });

        // Define color mapping for types
        const colorMap: { [key: string]: { base: string, dark: string } } = {
            'Bargeld': {base: 'rgba(0, 200, 150, 0.8)', dark: 'rgba(0, 140, 105, 1)'},
            'Kartenzahlung': {base: 'rgba(0, 150, 255, 0.8)', dark: 'rgba(0, 105, 180, 1)'},
            'Lieferung': {base: 'rgba(150, 80, 255, 0.8)', dark: 'rgba(105, 56, 180, 1)'},
        };

        // Default colors for unknown types
        const defaultColors = [
            {base: 'rgba(255, 140, 0, 0.8)', dark: 'rgba(180, 98, 0, 1)'},
            {base: 'rgba(255, 60, 120, 0.8)', dark: 'rgba(180, 42, 84, 1)'},
            {base: 'rgba(180, 180, 200, 0.8)', dark: 'rgba(126, 126, 140, 1)'},
        ];

        // Create datasets for each type (turnover as stacked bars)
        const datasets: any = Array.from(typeNames).map((typeName, index) => {
            const data = this.chartData.map(item => {
                const type = item.types.find((t: any) => t.name === typeName);
                return type ? parseFloat(type.turnover) : 0;
            });

            const colors = colorMap[typeName] || defaultColors[index % defaultColors.length];

            return {
                label: typeName,
                data: data,
                type: 'bar' as const,
                yAxisID: 'y',
                stack: 'turnover',
                backgroundColor: colors.base,
                borderColor: colors.base,
                order: 2,
            };
        });

        // Add sales datasets as line charts
        Array.from(typeNames).forEach((typeName, index) => {
            const data = this.chartData.map(item => {
                const type = item.types.find((t: any) => t.name === typeName);
                return type ? parseInt(type.sales) : 0;
            });

            const colors = colorMap[typeName] || defaultColors[index % defaultColors.length];

            datasets.push({
                label: `${typeName} (Belege)`,
                data: data,
                type: 'line' as const,
                yAxisID: 'y1',
                stack: undefined,
                backgroundColor: colors.dark,
                borderColor: colors.dark,
                order: 1,
            });
        });

        // Extract labels (dates)
        const labels = this.chartData.map(item => moment(item.date).format('dd, DD.MM.Y'));

        const component = this;

        this.chart = {
            type: 'bar',
            data: {
                labels: labels,
                datasets: datasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    datalabels: {
                        display: function (context: any) {
                            if (context.dataset.data[context.dataIndex] === 0) return false;
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: true,
                    },
                    y: {
                        stacked: true,
                        position: 'left',
                        title: {
                            display: true,
                            text: 'Umsatz (€)'
                        }
                    },
                    y1: {
                        position: 'right',
                        title: {
                            display: true,
                            text: 'Belege'
                        },
                        grid: {
                            drawOnChartArea: false,
                        }
                    }
                }
            }
        };
    }

    setCurrentTurnover(turnover: any = null) {
        this.showForm = true;

        // Initialize newTurnover with default values
        if (!turnover) {
            this.currentTurnover = {
                id_store: '',
                date: moment().subtract(1, 'month').endOf('month').format("YYYY-MM-DD"),
                type: 'delivery',
                value: null,
                count: 1,
            };
        } else {
            this.currentTurnover = {
                id_store: turnover.id_store,
                id_data_type: turnover.id_data_type,
                date: moment(turnover.date).format("YYYY-MM-DD"),
                type: turnover.data_type_identifier,
                value: turnover.turnover,
                count: turnover.sales,
                id_user: turnover.id_user,
            };
        }

        this.setAvailableTypes();
    }

    setAvailableTypes() {
        this.availableTypes = [
            {
                value: 'delivery',
                name: 'Lieferung'
            },
            {
                value: 'card',
                name: 'Kartenzahlung'
            },
            {
                value: 'cash',
                name: 'Bargeld'
            },
        ];

        /*this.storeData.forEach(data => {
            if (data.date == this.currentTurnover.date) {
                this.availableTypes = this.availableTypes.filter(type => type.value != data.data_type_identifier);
            }
        });*/
    }

    closeCurrentTurnover() {
        this.showForm = false;
    }

    saveTurnover() {
        this.dataService.request('Turnover/save', {
            id_store: this.currentTurnover.id_store,
            date: moment(this.currentTurnover.date).format("YYYY-MM-DD"),
            type: this.currentTurnover.type,
            value: this.currentTurnover.value,
            count: this.currentTurnover.count,
        }).subscribe(() => {
            this.toastrService.success('Umsatz wurde hinzugefügt');
            this.showForm = false;
            this.loadData();
        });
    }

    updateTurnover() {
        this.dataService.request('Turnover/update', {
            id_store: this.currentTurnover.id_store,
            id_data_type: this.currentTurnover.id_data_type,
            date: moment(this.currentTurnover.date).format("YYYY-MM-DD"),
            value: this.currentTurnover.value,
            count: this.currentTurnover.count,
        }).subscribe(() => {
            this.toastrService.success('Umsatz wurde aktualisiert');
            this.showForm = false;
            this.loadData();
        })
    }

    deleteTurnover(data: any) {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Umsatz wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Turnover/delete', {
                    id_store: data.id_store,
                    date: data.date,
                    id_data_type: data.id_data_type,
                }).subscribe(() => {
                    this.toastrService.success('Umsatz wurde gelöscht');
                    this.loadData();
                });
            }
        });
    }

    protected toggleData(data: any) {
        data.toggled = !data.toggled;

        this.toggleState[data.name] = data.toggled;
    }

    protected toggleAllData(toggled: boolean) {
        this.allToggled = toggled;
        this.storesData.forEach(store => store.toggled = this.allToggled);
    }

    toggleTurnoverLabels() {
        this.showTurnoverLabels = !this.showTurnoverLabels;

        if (this.baseChart?.chart) {
            this.baseChart.chart.data.datasets.forEach((dataset: any, index: number) => {
                if (dataset.type === 'bar') {
                    const meta = this.baseChart!.chart!.getDatasetMeta(index);
                    meta.hidden = !this.showTurnoverLabels;
                }
            });
            this.baseChart.chart.update();
        }
    }

    toggleSalesLabels() {
        this.showSalesLabels = !this.showSalesLabels;

        if (this.baseChart?.chart) {
            this.baseChart.chart.data.datasets.forEach((dataset: any, index: number) => {
                if (dataset.type === 'line') {
                    const meta = this.baseChart!.chart!.getDatasetMeta(index);
                    meta.hidden = !this.showSalesLabels;
                }
            });
            this.baseChart.chart.update();
        }
    }

    protected setTab(event, tab: string) {
        event.preventDefault();
        this.currentTab = tab;
    }
}
